import type {
  LedgerAccount,
  LedgerTransaction,
  TransactionKind,
} from '../types/finance'

export interface BalanceSet {
  available: number
  pending: number
  escrow: number
  withdrawable: number
}

const USER_ACCOUNTS: LedgerAccount[] = [
  'user:available',
  'user:pending',
  'user:escrow',
  'user:withdrawable',
]

/**
 * Balances are DERIVED from immutable ledger entries — never stored as a mutable
 * field. Reversals, refunds and adjustments are additional transactions, so they
 * fall out of this same computation without rewriting history.
 */
export function deriveBalances(transactions: LedgerTransaction[]): BalanceSet {
  const totals: Record<string, number> = {
    'user:available': 0,
    'user:pending': 0,
    'user:escrow': 0,
    'user:withdrawable': 0,
  }

  for (const transaction of transactions) {
    if (transaction.state === 'FAILED') continue
    for (const entry of transaction.entries) {
      if (!USER_ACCOUNTS.includes(entry.account)) continue
      const signed = entry.direction === 'credit' ? entry.amountNaira : -entry.amountNaira
      totals[entry.account] += signed
    }
  }

  return {
    available: totals['user:available'],
    pending: totals['user:pending'],
    escrow: totals['user:escrow'],
    withdrawable: totals['user:withdrawable'],
  }
}

export function isBalanced(transaction: LedgerTransaction): boolean {
  const [first, second] = transaction.entries
  return (
    first.amountNaira === second.amountNaira && first.direction !== second.direction
  )
}

export const ACCOUNT_LABEL: Record<LedgerAccount, string> = {
  'user:available': 'Your available balance',
  'user:pending': 'Your pending balance',
  'user:escrow': 'Escrow held for orders',
  'user:withdrawable': 'Your withdrawable balance',
  'provider:settlement': 'Provider settlement account',
  'platform:fees': 'Campuzon fee account',
  'counterparty:external': 'External counterparty',
}

export const KIND_LABEL: Record<TransactionKind, string> = {
  funding: 'Funding',
  transfer: 'Transfer',
  request: 'Money request',
  split: 'Expense split',
  gift: 'Gift',
  order_payment: 'Order payment',
  escrow_hold: 'Escrow hold',
  escrow_release: 'Escrow release',
  bill_payment: 'Bill payment',
  payout: 'Payout',
  fee: 'Platform fee',
  reversal: 'Reversal',
  refund: 'Refund',
}
